import React, { FC } from 'react';
import cs from 'classnames';
import { useTranslation } from '@wix/wix-i18n-config';
import { Drawer } from '@wix/design-system';
import { Dismiss } from '@wix/wix-ui-icons-common';
import { ChevronDown } from '@wix/wix-ui-icons-common/odeditor';

import { Accordion } from '../Accordion/Accordion';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { useMonitoring } from '../../contexts/MonitoringContext';
import { useMenuCategoryGroups } from '../../hooks/useMenuCategoryGroups';
import { usePageScroll } from '../../contexts/PageScrollContext';
import { useTemplatesPageUI } from '../../contexts/TemplatesPageUIContext';
import { TemplateBuilderSwitch } from '../TemplateBuilderSwitch/TemplateBuilderSwitch';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import variables from '../../styles/_variables.scss';

import { bottomNavigationMenuDataHooks as dataHooks } from './BottomNavigationMenu.dataHooks';

import Icon from '../../icons/MenuToggle.svg';
import s from './BottomNavigationMenu.scss';
import { useIsStudio2Ui } from '../../hooks/useIsStudio2Ui';

export const BottomNavigationMenu: FC = () => {
  const { isCategoriesMenuOpen, toggleCategoriesMenu } = useTemplatesPageUI();
  const { t } = useTranslation();
  const { reportBrowseSidebar } = useBiLogger();
  const monitoring = useMonitoring();
  const categoryGroups = useMenuCategoryGroups();
  const { scrollToTop } = usePageScroll();
  const isStudio2Ui = useIsStudio2Ui();
  // on desktop the same state opens the sticky bar's mega menu on hover, not this drawer
  const isBelowDesktop = useMediaQuery(
    `(max-width: ${parseInt(variables.screenWidthSmallDesktop, 10) - 1}px)`,
  );

  const accordionData = categoryGroups.map(({ title, categories }) => ({
    title,
    content: categories.map((menuCategory) => ({
      url: menuCategory.url,
      active: menuCategory.isActive,
      label: menuCategory.label,
      id: menuCategory.slug,
    })),
  }));

  const accordion = (
    <Accordion
      data={accordionData}
      onItemClick={(bucketName: string) => reportBrowseSidebar(bucketName)}
      onSubitemClick={(categoryName: string) => {
        reportBrowseSidebar(categoryName);
        monitoring.startCategorySwitchInteraction();
        scrollToTop();
        toggleCategoriesMenu(false);
      }}
    />
  );

  if (isStudio2Ui) {
    return (
      <>
        <div className={s.mobile}>
          <button
            className={s.categoriesButton}
            onClick={() => toggleCategoriesMenu(true)}
            data-hook={dataHooks.categoriesButton()}
          >
            {t('app.topBar.menu.toggle.label')}
            <ChevronDown />
          </button>
          <TemplateBuilderSwitch />
        </div>
        {/* above the site header (z-index 9999) */}
        <Drawer
          open={isCategoriesMenuOpen && isBelowDesktop}
          onClose={() => toggleCategoriesMenu(false)}
          zIndex={10000}
        >
          <div data-hook={dataHooks.container()}>{accordion}</div>
        </Drawer>
      </>
    );
  }

  const browseBy = (
    <span className={s.toggle}>
      <Icon />
      <span>{t('app.topBar.menu.toggle.label')}</span>
    </span>
  );

  return (
    <>
      <div className={cs(s.mobile)} onClick={() => toggleCategoriesMenu(true)}>
        {browseBy}
      </div>
      <div
        data-hook={dataHooks.container()}
        className={cs(s.navigationMenu, {
          [s.open]: isCategoriesMenuOpen,
        })}
      >
        {isCategoriesMenuOpen && (
          <div
            onClick={() => toggleCategoriesMenu(false)}
            className={s.backdrop}
          />
        )}
        <div className={s.content}>
          {isCategoriesMenuOpen && (
            <button
              className={s.dismiss}
              onClick={() => toggleCategoriesMenu(false)}
            >
              <Dismiss color="black" />
            </button>
          )}
          {accordion}
        </div>
      </div>
    </>
  );
};
